import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    addCountriesSelection,
    getSelectedCountries,
    CountryState,
    getCountries,
    getCountryState,
    clearFilters,
    updateCheckboxes,
    searchCountries,
    setCheckboxes,
    getCheckboxState,
} from '@emi/shared/state/filters';
import { CheckboxTreeNode, Country } from '@emi/shared/models';
import { Subject, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import * as Constants from '@emi/shared/constants';

@Component({
    selector: 'via-filter-countries',
    templateUrl: './filter-countries.component.html',
    styleUrls: ['./filter-countries.component.scss'],
})
export class FilterCountriesComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public onApplyClicked = new EventEmitter();

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    private countriesSubscription: Subscription;
    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    private checkBoxSubscription: Subscription;
    private stateSubscription: Subscription;
    public countriesTree: Array<CheckboxTreeNode> = [];
    public selectedCountries: Array<Country> = [];
    public stateCountries: Array<Country> = [];
    public loading: boolean;
    public showError = false;
    public checkedArray: Array<CheckboxTreeNode> = [];
    public errorMessage = '';
    public searchText: string | null;
    public eventsSubject: Subject<CheckboxTreeNode[]> = new Subject<CheckboxTreeNode[]>();
    public noResultsMessage = Constants.GlobalVariables.NO_SEARCH_RESULTS;

    constructor(private readonly store: Store<CountryState>) {}

    public onCheckboxStateChange(selectedCheckbox: CheckboxTreeNode): void {
        this.store.dispatch(updateCheckboxes({ checkbox: selectedCheckbox }));
    }

    public ngOnInit(): void {
        this.checkBoxSubscription = this.store.pipe(select(getCheckboxState)).subscribe((y) => {
            this.checkedArray = y.checkboxes;
        });

        this.countriesSubscription = this.store.pipe(select(getSelectedCountries)).subscribe((countries) => {
            if (countries.length > 0) {
                if (this.checkedArray.length === 0) {
                    this.store.dispatch(
                        setCheckboxes({
                            checkboxes: countries.map((country) => {
                                return this.buildCheckboxTreeNode(country, true);
                            }),
                        })
                    );
                }
                this.selectedCountries = countries;
                this.countriesTree = countries.map((country) => {
                    return this.buildCheckboxTreeNode(country, true);
                });
            }
        });

        this.stateSubscription = this.store.pipe(select(getCountryState)).subscribe((state) => {
            this.loading = state.loading;
            this.showError = state.failed;
            this.errorMessage = state.error;
            this.searchText = state.searchText;
            if (this.selectedCountries.length === 0) {
                this.countriesTree = this.searchText
                    ? state.searchedCountries.map((country) => {
                          return this.buildCheckboxTreeNode(country);
                      })
                    : state.countries.map((country) => {
                          return this.buildCheckboxTreeNode(country);
                      });
            }
            this.eventsSubject.next(this.countriesTree);
            this.stateCountries = state.countries;
        });

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            const countries: Array<Country> = this.checkedArray
                .map((country) => new Country(country.id, country.label,'','',''))
                .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
            this.store.dispatch(addCountriesSelection({ countries }));
            this.store.dispatch(getCountries());
            this.store.dispatch(clearFilters());
            this.onApplyClicked.emit();
        });

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.store.dispatch(
                    setCheckboxes({
                        checkboxes: this.selectedCountries.map((country) => {
                            return this.buildCheckboxTreeNode(country, true);
                        }),
                    })
                );
                const currentSelectableCountries = this.selectedCountries.length > 0 ? this.selectedCountries : this.stateCountries;
                this.countriesTree = currentSelectableCountries.map((country) => {
                    return this.buildCheckboxTreeNode(country, this.selectedCountries.length > 0);
                });
                this.eventsSubject.next(this.countriesTree);
            });
        }

        if (this.selectedCountries.length === 0 && this.stateCountries.length === 0) {
            this.store.dispatch(getCountries());
        }
    }

    public buildCheckboxTreeNode(country: Country, completed?: boolean): CheckboxTreeNode {
        if (completed) {
            return new CheckboxTreeNode(country.id, country.name, 'checked');
        }
        const existingCountry = this.checkedArray.find((x) => x.id === country.id);
        return new CheckboxTreeNode(country.id, country.name, existingCountry ? 'checked' : 'unchecked');
    }

    public selectionChanged(): void {
        this.showApplyButtonChange.emit(this.selectedCountries.length !== this.checkedArray.length && this.checkedArray.length > 0);
    }

    public searchFiltersByText(text: string): void {
        this.store.dispatch(searchCountries({ searchText: text, delay: Constants.GlobalVariables.FILTER_SEARCH_DELAY }));
    }

    public resetSearch(): void {
        this.store.dispatch(searchCountries({ searchText: '', delay: 0 }));
    }

    public ngOnDestroy(): void {
        if (this.searchText) {
            this.store.dispatch(searchCountries({ searchText: '', delay: 0 }));
        }
        this.checkBoxSubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.countriesSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        if (this.isOverlay) {
            this.cancelClickedSubscription.unsubscribe();
        }
        this.showApplyButtonChange.emit(false);
    }
}
